import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Slider,
  Chip,
} from '@mui/material';
import { Building2, X } from 'lucide-react';

const PropertyFiltersDialog = ({
  onClose,
  onApply,
  onClear,
  initialFilters,
}) => {
  const [propertyTypes, setPropertyTypes] = useState(
    initialFilters?.propertyTypes || [],
  );
  const [unitCount, setUnitCount] = useState(
    initialFilters?.unitCount || [5, 500],
  );
  const [stories, setStories] = useState(initialFilters?.stories || [1, 20]);
  const [yearBuilt, setYearBuilt] = useState(
    initialFilters?.yearBuilt || [1980, 2024],
  );
  const [amenities, setAmenities] = useState(initialFilters?.amenities || []);

  const handleApply = () => {
    onApply({
      propertyTypes,
      unitCount,
      stories,
      yearBuilt,
      amenities,
    });
    onClose();
  };

  const togglePropertyType = type => {
    setPropertyTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type],
    );
  };

  const toggleAmenity = amenity => {
    setAmenities(prev =>
      prev.includes(amenity)
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity],
    );
  };

  const propertyTypeOptions = [
    { id: 'mf5-19', label: 'Small Multifamily', description: '5-19 units' },
    {
      id: 'mf20-49',
      label: 'Mid-size Multifamily',
      description: '20-49 units',
    },
    { id: 'mf50-99', label: 'Large Multifamily', description: '50-99 units' },
    {
      id: 'mf100-199',
      label: 'Very Large Multifamily',
      description: '100-199 units',
    },
    {
      id: 'mf200',
      label: 'Institutional Multifamily',
      description: '200+ units',
    },
    { id: 'duplex', label: 'Duplex/4-plex', description: '2-4 units' },
    { id: 'sfr', label: 'SFR/BTR', description: 'Single-family rentals' },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Box
              sx={{
                padding: '0.5rem',
                backgroundColor: '#F0F4F5',
                borderRadius: '0.5rem',
              }}
            >
              <Building2 size={20} color='#39585C' />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                Property Filters
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  marginTop: '0.25rem',
                }}
              >
                Configure property type and characteristics
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size='small'>
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            paddingY: '1rem',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 500,
                marginBottom: '0.75rem',
              }}
            >
              Property Types
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: '0.75rem',
              }}
            >
              {propertyTypeOptions.map(type => (
                <Card
                  key={type.id}
                  onClick={() => togglePropertyType(type.id)}
                  sx={{
                    padding: '1rem',
                    cursor: 'pointer',
                    border: propertyTypes.includes(type.id)
                      ? '2px solid #39585C'
                      : '1px solid #E5E7EB',
                    backgroundColor: propertyTypes.includes(type.id)
                      ? '#F0F4F5'
                      : '#FFFFFF',
                    '&:hover': { boxShadow: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'start',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                      >
                        {type.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          marginTop: '0.25rem',
                        }}
                      >
                        {type.description}
                      </Typography>
                    </Box>
                    {propertyTypes.includes(type.id) && (
                      <Chip
                        label='✓'
                        size='small'
                        sx={{ backgroundColor: '#39585C', color: '#FFFFFF' }}
                      />
                    )}
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.75rem',
              }}
            >
              <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                Unit Count
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                {unitCount[0]} - {unitCount[1]}+ units
              </Typography>
            </Box>
            <Slider
              value={unitCount}
              onChange={(e, value) => setUnitCount(value)}
              min={1}
              max={500}
              step={5}
              valueLabelDisplay='auto'
            />
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.75rem',
              }}
            >
              <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                Stories
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                {stories[0]} - {stories[1]} stories
              </Typography>
            </Box>
            <Slider
              value={stories}
              onChange={(e, value) => setStories(value)}
              min={1}
              max={20}
              step={1}
              valueLabelDisplay='auto'
            />
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.75rem',
              }}
            >
              <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                Year Built
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                {yearBuilt[0]} - {yearBuilt[1]}
              </Typography>
            </Box>
            <Slider
              value={yearBuilt}
              onChange={(e, value) => setYearBuilt(value)}
              min={1980}
              max={2024}
              step={1}
              valueLabelDisplay='auto'
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 500,
                marginBottom: '0.75rem',
              }}
            >
              Amenities
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: '0.75rem',
              }}
            >
              {[
                { id: 'pool', label: 'Pool' },
                { id: 'fitness', label: 'Fitness Center' },
                { id: 'package-lockers', label: 'Package Lockers' },
                { id: 'parking', label: 'Covered Parking' },
                { id: 'clubhouse', label: 'Clubhouse' },
                { id: 'business-center', label: 'Business Center' },
                { id: 'pet-friendly', label: 'Pet Friendly' },
                { id: 'ev-charging', label: 'EV Charging' },
              ].map(amenity => (
                <FormControlLabel
                  key={amenity.id}
                  control={
                    <Checkbox
                      checked={amenities.includes(amenity.id)}
                      onChange={() => toggleAmenity(amenity.id)}
                      size='small'
                    />
                  }
                  label={amenity.label}
                  sx={{ margin: 0 }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB' }}
      >
        {onClear && (
          <Button
            onClick={() => {
              onClear();
              onClose();
            }}
            sx={{
              textTransform: 'none',
              color: '#6B7280',
              marginRight: 'auto',
              '&:hover': { color: '#39585C' },
            }}
          >
            Clear Filters
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleApply}
          sx={{
            textTransform: 'none',
            backgroundColor: '#39585C',
            '&:hover': {
              backgroundColor: '#2d4649',
            },
          }}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyFiltersDialog;
